import { type NoteGenerationTimings } from '@/features/note-generation/noteGenerationEventTracking';
import {
  resetSideBySideAction,
  sideBySideSetupAction,
  skipSideBySideAction,
  streamNoteCandidatesAction,
} from '@/features/sideBySideNoteGen/atoms';
import { waitForAtom } from '@/utils/jotaiHelpers';
import { sanitizeError } from '@heidi/runtime/logger';
import { type components } from '@heidi/schema/ml-scribe';
import {
  claimGenerationPostActions,
  finishSessionGeneration,
  isCurrentGeneration,
  noteGenerationLogger,
  shouldDoSideBySideGenerationAtomFamily,
  sideBySideSessionStateAtomFamily,
  type GenerationHandle,
  type SideBySideConsultNoteCandidateWithStream,
} from '@heidi/session/data';
import { getDefaultStore } from 'jotai';

const jotaiStore = getDefaultStore();
const SIDE_BY_SIDE_SELECTION_TIMEOUT_MS = 30 * 60 * 1000;

export type SideBySideFlowInput = {
  sessionID: string;
  /** The body's registered handle, passed by reference. */
  handle: GenerationHandle;
  /** Resolved by the body; setup adds only window_width. */
  requestBody: components['schemas']['GenerateConsultNoteRequest'];
  /** Reads chunk-recording eligibility at call time with the body's signal. */
  waitForUploads: () => Promise<void>;
  /** Updates the body's timings synchronously so its abort handler sees them. */
  markTiming: (phase: 'waitingUploadStart' | 'waitingUploadEnd') => NoteGenerationTimings;
  refreshFeatureFlags: () => void;
  /** Publishes chosen-note success before post actions; this flow owns the claim. */
  runPostGenerationActions: (selection: { isNoteChosen: boolean }) => void;
};

export type SideBySideFlowResult =
  | { kind: 'aborted' }
  | { kind: 'delegated' }
  | {
      kind: 'single-note';
      candidates: SideBySideConsultNoteCandidateWithStream[] | null;
      chunksAlreadyUploaded: true;
    };

/**
 * Same-line extraction of the body's SBS fork. The child SBS actions remain
 * the sole phase-machine owner. Throws reach the body's deferred failure boundary
 * unchanged; cancellation classification and outcome recording stay in the body.
 */
export async function runSideBySideFlow({
  sessionID,
  handle,
  requestBody,
  waitForUploads,
  markTiming,
  refreshFeatureFlags,
  runPostGenerationActions,
}: SideBySideFlowInput): Promise<SideBySideFlowResult> {
  let candidates: SideBySideConsultNoteCandidateWithStream[] | null = null;

  // Show loading animation immediately while waiting for chunks.
  const sessionSbsStateAtom = sideBySideSessionStateAtomFamily(sessionID);
  jotaiStore.set(sessionSbsStateAtom, {
    ...jotaiStore.get(sessionSbsStateAtom),
    setupLoading: true,
  });
  markTiming('waitingUploadStart');
  try {
    await waitForUploads();
  } catch (err) {
    if (handle.controller.signal.aborted) {
      jotaiStore.set(resetSideBySideAction, handle);
      finishSessionGeneration(handle);
      return { kind: 'aborted' };
    }
    noteGenerationLogger.error(
      '[_streamResponse] SBS chunk upload wait failed, proceeding with note generation',
      {
        sessionID,
        errorName: err instanceof Error ? err.name : typeof err,
        error: sanitizeError(err),
      }
    );
  }
  const timingsAfterUpload = markTiming('waitingUploadEnd');
  /**
   * Force reload PostHog feature flags to prevent caching issues.
   * This ensures the backend receives the latest flag values when determining
   * which model/variant to route traffic to during side-by-side generation.
   * Without this, cached flags can cause traffic misrouting for 1-6 hours after flag changes.
   * Throttled by the body's FLAG_REFRESH_MIN_INTERVAL_MS.
   */
  refreshFeatureFlags();

  /**
   * Check if we want to do a side by side generation
   */
  try {
    candidates =
      (await jotaiStore.set(sideBySideSetupAction, {
        ...handle,
        reqBody: {
          ...requestBody,
          window_width: window.innerWidth,
        },
      })) ?? null;
  } catch (error) {
    if (handle.controller.signal.aborted) {
      jotaiStore.set(resetSideBySideAction, handle);
      finishSessionGeneration(handle);
      return { kind: 'aborted' };
    }
    throw error;
  }
  if (
    jotaiStore.get(shouldDoSideBySideGenerationAtomFamily(sessionID)) &&
    isCurrentGeneration(handle)
  ) {
    noteGenerationLogger.info(
      '[SIDE_BY_SIDE] Recevied >1 note candidates, we should do Side By Side now'
    );
    await jotaiStore.set(streamNoteCandidatesAction, {
      ...handle,
      timings: timingsAfterUpload,
    });

    // A Stop or supersession is excluded by the post-action claim guard.
    let isNoteChosen = false;
    try {
      const settled = await waitForAtom(
        sideBySideSessionStateAtomFamily(sessionID),
        (state) =>
          state.generationId !== handle.generationId || state.candidates.length === 0,
        {
          timeoutMs: SIDE_BY_SIDE_SELECTION_TIMEOUT_MS,
          signal: handle.controller.signal,
        }
      );
      isNoteChosen = settled.phase !== 'failed';
    } catch (error) {
      if (
        isCurrentGeneration(handle) &&
        error instanceof Error &&
        error.message.startsWith('[TIMEOUT]')
      ) {
        // Selection expiry intentionally chooses the default note.
        isNoteChosen = jotaiStore.set(skipSideBySideAction, sessionID);
      } else if (!handle.controller.signal.aborted) {
        noteGenerationLogger.error(
          '[SIDE_BY_SIDE] error while waiting for candidate picking in sbs',
          {
            errorName: error instanceof Error ? error.name : typeof error,
            error: sanitizeError(error),
          }
        );
      }
    } finally {
      if (claimGenerationPostActions(handle)) {
        runPostGenerationActions({ isNoteChosen });
      }
      finishSessionGeneration(handle);
    }

    return { kind: 'delegated' };
  }
  return { kind: 'single-note', candidates, chunksAlreadyUploaded: true };
}
